import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type AnalisisCausa = components['schemas']['AnalisisRead']
export type AnalisisNodo = components['schemas']['NodoRead']
export type AnalisisSugerencia = components['schemas']['AnalisisSugerenciaRead']
export type Metodologia = components['schemas']['InvestigacionMetodologia']
export type CausaCategoria = components['schemas']['CausaCategoria']

function analisisUrl(businessId: string, actionId: string) {
  return `/businesses/${businessId}/sgsst/actions/${actionId}/analisis`
}

/** `null` cuando la acción no tiene análisis: la ausencia es el estado normal, no un error. */
export function useAnalisisCausa(businessId: string | undefined, actionId: string | undefined) {
  return useQuery({
    queryKey: ['analisis-causa', businessId, actionId],
    queryFn: async () => (await api.get<AnalisisCausa | null>(analisisUrl(businessId!, actionId!))).data,
    enabled: !!businessId && !!actionId,
  })
}

function useAnalisisInvalidation(businessId: string, actionId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['analisis-causa', businessId, actionId]})
    // El plan muestra `tiene_analisis` y las sugerencias se apagan cuando el análisis existe.
    queryClient.invalidateQueries({queryKey: ['actions', businessId]})
    queryClient.invalidateQueries({queryKey: ['analisis-sugerencias', businessId]})
  }
}

export function useAbrirAnalisis(businessId: string, actionId: string) {
  const invalidate = useAnalisisInvalidation(businessId, actionId)
  return useMutation({
    mutationFn: async (metodologia: Metodologia) =>
      (await api.post<AnalisisCausa>(analisisUrl(businessId, actionId), {metodologia})).data,
    onSuccess: () => {
      invalidate()
      track('analisis_causa_abierto', {business_id: businessId})
    },
  })
}

export function useCambiarMetodologia(businessId: string, actionId: string) {
  const invalidate = useAnalisisInvalidation(businessId, actionId)
  return useMutation({
    mutationFn: async (metodologia: Metodologia) =>
      (await api.patch<AnalisisCausa>(analisisUrl(businessId, actionId), {metodologia})).data,
    onSuccess: invalidate,
  })
}

export function useDescartarAnalisis(businessId: string, actionId: string) {
  const invalidate = useAnalisisInvalidation(businessId, actionId)
  return useMutation({
    mutationFn: async () => api.delete(analisisUrl(businessId, actionId)),
    onSuccess: invalidate,
  })
}

export type NodoInput = {descripcion: string; espina?: CausaCategoria | null; parent_nodo_id?: string | null}

export function useAgregarCausa(businessId: string, actionId: string) {
  const invalidate = useAnalisisInvalidation(businessId, actionId)
  return useMutation({
    mutationFn: async (input: NodoInput) =>
      (await api.post<AnalisisCausa>(`${analisisUrl(businessId, actionId)}/nodos`, input)).data,
    onSuccess: invalidate,
  })
}

export function useQuitarCausa(businessId: string, actionId: string) {
  const invalidate = useAnalisisInvalidation(businessId, actionId)
  return useMutation({
    mutationFn: async (nodoId: string) =>
      (await api.delete<AnalisisCausa>(`${analisisUrl(businessId, actionId)}/nodos/${nodoId}`)).data,
    onSuccess: invalidate,
  })
}

export function useAnalisisSugerencias(businessId: string | undefined, year?: number) {
  return useQuery({
    queryKey: ['analisis-sugerencias', businessId, year ?? 'current'],
    queryFn: async () =>
      (
        await api.get<AnalisisSugerencia[]>(`/businesses/${businessId}/sgsst/analisis-sugerencias`, {
          params: year ? {year} : {},
        })
      ).data,
    enabled: !!businessId,
  })
}

export function useDescartarSugerencia(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (actionId: string) =>
      api.post(`/businesses/${businessId}/sgsst/actions/${actionId}/analisis-sugerencia/descartar`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['analisis-sugerencias', businessId]})
      track('analisis_sugerencia_descartada', {business_id: businessId})
    },
  })
}
